import { timeout } from '@art-suite/art-core-ts';
import { ApiScan } from '@kaiizen/kai-pro-api';
import {
  getOptimizedScan,
  getScanRootFromScanData,
  getScanRootWithScanDataUpdates,
  ScanDataInMemory,
  ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';
import { useCombinedCachedQueryStatus } from '@kaiizen/react-persistent-cache';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useScanEditSyncGate } from './ScanEditSyncGate';
import { useScanData } from './useScanData';
import { useScanTemplateRootForScan } from './useScanTemplateRoot';

/**
 * Hook that returns the optimized scan root data for a given scan.
 * Edits update optimizedScanRoot with setOptimizedScanRoot, which keeps every unchanged part's
 * objects and so avoids full re-renders. It is rebuilt only from scanData the screen doesn't
 * already show: data fetched from the server, or written by another window. While an edit waits to
 * save, the rebuild waits too, until the edit's save has merged into that data.
 * @param scan - The scan to get the optimized scan root data for.
 * @returns The optimized scan root data.
 */
export const useOptimizedScanRoot = (scan: ApiScan | undefined) => {
  const scanId = scan?.id;
  const { scanTemplateRoot, ...scanTemplateRootStatusInfo } = useScanTemplateRootForScan(scan);
  const editSyncGate = useScanEditSyncGate(scanId);
  const { scanData, baselineData, deltaData, updateCachedScanData, ...scanDataStatusInfo } = useScanData(scan, { editSyncGate });
  // Every scanData the on-screen optimizedScanRoot already reflects: ones it was built from, and
  // ones this hook wrote for edits already on screen. Rebuilding from one of those can only drop
  // edits made since. A set rather than just the last one, like useSaveScanData's
  // ownOptimizedScanRootsRef: a render can lag behind the writes (KAIDEV-4328).
  const reflectedScanDataRef = useRef(new WeakSet<ScanDataInMemory[]>());
  // Set while a rebuild waits for this window's pending edit to save. The data that save writes
  // then also holds the change the rebuild waits for, which the screen doesn't show yet.
  const rebuildDeferredRef = useRef(false);
  const [deferredRebuildRetries, retryDeferredRebuild] = useReducer((retries: number) => retries + 1, 0);

  const [optimizedScanRoot, setOptimizedScanRoot] = useState<ScanRootOptimized | undefined>(undefined);
  const [optimizedBaselineScanRoot, setOptimizedBaselineScanRoot] = useState<ScanRootOptimized | undefined>(undefined);

  const optimizedScanRootRef = useRef<ScanRootOptimized | undefined>(optimizedScanRoot);
  optimizedScanRootRef.current = optimizedScanRoot;
  const optimizedBaselineScanRootRef = useRef<ScanRootOptimized | undefined>(optimizedBaselineScanRoot);
  optimizedBaselineScanRootRef.current = optimizedBaselineScanRoot;

  useEffect(() => {
    optimizedScanRootRef.current = undefined;
    optimizedBaselineScanRootRef.current = undefined;
    reflectedScanDataRef.current = new WeakSet();
    rebuildDeferredRef.current = false;
    setOptimizedScanRoot(undefined);
    setOptimizedBaselineScanRoot(undefined);
  }, [scanId]);

  // this one is simple, we only load the baseline scan root once
  useEffect(() => {
    if (baselineData && scanTemplateRoot) {
      const baselineScanRoot = getScanRootFromScanData(baselineData);
      const optimizedBaselineScanRoot = getOptimizedScan(baselineScanRoot, scanTemplateRoot);
      setOptimizedBaselineScanRoot(optimizedBaselineScanRoot);
    }
  }, [baselineData, scanId, scanTemplateRoot]);

  useEffect(() => {
    if (scanData && scanTemplateRoot && !reflectedScanDataRef.current.has(scanData)) {
      let isCancelled = false;
      // we use timeout here to let ReactNative get a render cycle before we do the computational heavy work of initializing the optimized scan root
      timeout(10).then(() => {
        if (isCancelled) return;
        // This window has an edit waiting to save. Rebuilding now would drop it from the screen, and
        // its save would then also revert every part this data changed (KAIDEV-3162). Instead,
        // rebuild once the save has merged the edit into this data.
        if (optimizedScanRootRef.current && editSyncGate.hasPendingLocalEdit()) {
          rebuildDeferredRef.current = true;
          editSyncGate.whenNoPendingLocalEdit(retryDeferredRebuild);
          return;
        }
        const optimizedBaselineScanRoot = optimizedBaselineScanRootRef.current;
        const optimizedScanRoot = optimizedBaselineScanRoot && deltaData
          // if we have a baseline scan root and delta data, we can use the getScanRootWithScanDataUpdates function to get the optimized scan root
          ? getScanRootWithScanDataUpdates(optimizedBaselineScanRoot, deltaData)
          // otherwise, we just use the scan data to get the optimized scan root
          : getOptimizedScan(getScanRootFromScanData(scanData), scanTemplateRoot);
        if (isCancelled) return;
        rebuildDeferredRef.current = false;
        setOptimizedScanRoot(optimizedScanRoot);
        reflectedScanDataRef.current.add(scanData);
      });
      return () => {
        isCancelled = true;
      };
    }
  }, [scanData, scanId, scanTemplateRoot, editSyncGate, optimizedBaselineScanRoot, deferredRebuildRetries]);

  const updateCachedScanDataFromScreen = useCallback(async (newScanData: ScanDataInMemory[]) => {
    const scanDataWritten = await updateCachedScanData(newScanData);
    if (!rebuildDeferredRef.current) reflectedScanDataRef.current.add(scanDataWritten);
  }, [updateCachedScanData]);

  return {
    optimizedScanRoot,
    optimizedBaselineScanRoot,
    updateCachedScanData: updateCachedScanDataFromScreen,
    setOptimizedScanRoot,
    editSyncGate,
    ...useCombinedCachedQueryStatus(scanDataStatusInfo, scanTemplateRootStatusInfo),
  };
};
